import React, { useState, useEffect } from "react";
import axios from "axios";
import "../../App.css";
import { useLanguage } from "../../context/LanguageContext";
import Swal from "sweetalert2";


const TopBar = ({ onSave, onExit, canvasDataUrl }) => {
  const [productId, setProductId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [finalImage, setFinalImage] = useState(null);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const { language, setLanguage, t } = useLanguage();
  const [quantity, setQuantity] = useState(1); 

  // 🧩 Parse product_id from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pid = params.get("product_id");
    const qty = params.get("quantity"); // Grab the qty we added to the WordPress link

    if (pid) setProductId(parseInt(pid));
    if (qty) setQuantity(parseInt(qty)); // Set the dynamic quantity
  }, []);


  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setShowSettings(false);
  };

  // 🖼️ Generate image directly from <canvas> if prop missing
  const getCanvasImage = () => {
    // Try prop first
    if (canvasDataUrl) return canvasDataUrl;

    // Try to capture from Konva canvas
    const konvaCanvas = document.querySelector("canvas");
    if (konvaCanvas) {
      const dataUrl = konvaCanvas.toDataURL("image/png");
      console.log("🧠 Captured canvas image:", dataUrl.substring(0, 80));
      return dataUrl;
    }

    console.warn("⚠️ No canvas found to capture!");
    return null;
  };

  const checkMissingImages = async () => {
    if (window.checkMissingPages) {
      return window.checkMissingPages();
    }
    return [];
  };


  // 🛒 Add to Cart API call
  const handleAddToCart = async () => {
    if (!productId) return alert("No product ID found!");

    // ✅ Use the globally exposed validation function (works for single or multi-page products)
    const missing = window.checkMissingPages ? window.checkMissingPages() : [];

    if (missing.length > 0) {
      // Show a warning but give the user the option to proceed,
      // which is the expected behavior for an Add to Cart action.
      const result = await Swal.fire({
        title: "Missing Content", // Generic title for both text and images
        html: `
            <p>The following areas do not have content:</p>
            <strong>${missing.join(", ")}</strong><br><br>
            <p>Please add content or continue anyway.</p>
            `,
        icon: "warning",
        iconColor: "#007AFF",
        showCancelButton: true,
        confirmButtonText: "Continue to Add to Cart",
        confirmButtonColor: "#007AFF",
        cancelButtonText: "Go Back to Edit",
      });

      if (!result.isConfirmed) {
        return; // user cancelled → return to editor
      }
    }

    // 🧩 Re-fetch images to ensure updated state after potential upload
    let images = [];
    if (window.getAllProductCanvasImages) {
      // This is primarily for multi-page products (like calendars)
      images = await window.getAllProductCanvasImages();
    }

    if (!images || images.length === 0) {
      // Fallback for single-page canvas products (like the one using getCanvasImage)
      const data = getCanvasImage(); // Assuming getCanvasImage() is defined
      if (data) images = [{ name: "cover", data }];
    }

    if (!images || images.length === 0) return alert("No image data available.");

    const pageNames = [
      "cover", "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December", "back"
    ];

    images.forEach((img, i) => {
      if (!img.name) img.name = pageNames[i] || `page-${i + 1}`;
    });

    setLoading(true); // Assuming setLoading is defined
    setLoadingPercent(0); // Assuming setLoadingPercent is defined

    // ✅ Compute or get subtotal
    let subtotal = 0;

    // 1) If global subtotal function exists, use it
    if (window.subtotal) {
      subtotal = window.subtotal();
    }

    // 2) Otherwise read price from the URL
    if (!subtotal || subtotal === 0) {
      const params = new URLSearchParams(window.location.search);
      const urlPrice = parseFloat(params.get("price"));
      if (!isNaN(urlPrice)) {
        subtotal = urlPrice;
      }
    }


    try {
      const payload = {
        product_id: productId,
        quantity: quantity,
        images: images.map((img) => ({ name: img.name, data: img.data })),
        order_data: {
          pages_count: images.length,
          subtotal: subtotal,
        },
      };

      console.log("📤 Add-to-Cart Payload:", payload);

      const response = await axios.post( // Assuming axios is imported
        "https://opticonframing.com.au/wp-json/cards/v1/add-to-cart",
        payload,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setLoadingPercent(percent);
            }
          },
        }
      );

      if (response.data.success) {
        window.location.href = "https://opticonframing.com.au/cart/?t=" + Date.now();
      } else {
        alert("Failed to add to cart.");
      }
    } catch (err) {
      console.error("❌ Add-to-Cart Error:", err);
      alert("Failed to add to cart. Check console for details.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <div className="canvas-topbar">
        <div className="canvas-topbar-left">
          <button className="canvas-topbar-btn icon-btn" onClick={onExit} title={t("return")}></button>
          <span style={{ marginLeft: "10px" }}>{t("title")}</span>
        </div>

        <div className="canvas-topbar-right">
          <div className="settings-dropdown">
            <button
              className="canvas-topbar-btn icon-btn"
              onClick={() => setShowSettings(!showSettings)}
            >
              <i className="fa-solid fa-gear"></i> {t("settings")}
            </button>

            {showSettings && (
              <div className="settings-menu">
                <h4>{t("selectLanguage")}</h4>
                <ul>
                  <li onClick={() => handleLanguageChange("en")}>🇬🇧 English</li>
                  <li onClick={() => handleLanguageChange("fr")}>🇫🇷 Français</li>
                  <li onClick={() => handleLanguageChange("da")}>🇩🇰 Dansk</li>
                  <li><hr /></li>
                  <li style={{ cursor: "pointer" }} onClick={() => setShowSettings(false)}> <i class="fa-solid fa-circle-left"></i>&nbsp; Return</li>
                </ul>
              </div>
            )}
          </div>

          <button className="canvas-topbar-btn icon-btn" onClick={onSave}>
            <i className="fa-solid fa-floppy-disk"></i> {t("save")}
          </button>

          <button className="canvas-topbar-btn next-btn" onClick={handleAddToCart}>
            {t("next")} <i className="fa-solid fa-arrow-right"></i>
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <div className="loading-percent">{loadingPercent}%</div>
        </div>
      )}

    </>
  );
};

export default TopBar;